/** @jsxImportSource @emotion/react */

import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import SupplierCardComponent from '@SupplierCard';
import AnimationWrapper from '@AnimationWrapper';
import { Box } from '@mui/material';
import { getLinkWithTrackingParameters } from '../../Helpers/supplierCard';
import { resolvePrimarySupplier } from '../../Helpers/supplierResolution';
import { reportWhereMissingUsps } from '../../Helpers/reportWhereMissingUsps';
import { getSelectAndConnectComponent } from '../../Helpers/supplierMatchPredicates';
import { hashString } from '../../Utils/utils';
import useGetExperimentName from '../../Hooks/useGetExperimentName/useGetExperimentName';
/**
 * SupplierCard Component
 *
 * Renders a card for a supplier with a logo, USPs, and a CTA button.
 * When this component appears on a page forward navigation is blocked.
 * The CTA will open the cta link, appending tracking parameters when that link is to appwiki.
 *
 */
const SupplierCard = ({ componentId }) => {
  const components = useSelector((store) => store.components);
  const component = components[`component_${componentId}`];
  const { pageUrl, formId, subcategoryId } = useSelector(
    (store) => store.formConfig
  );
  const dynamicSelectAndConnect = useSelector(
    (store) => store.features?.dynamicSelectAndConnect
  );
  const dynamicMatches = useSelector(
    (store) => store.supplierMatches?.dynamicMatches
  );
  const animationExperimentName = useGetExperimentName({
    componentId,
    componentName: 'AnimationWrapper',
  });
  const supplierCardExperimentName = useGetExperimentName({
    componentId,
    componentName: 'SupplierCard',
  });

  // The generic fallback is configured on the Select and Connect component, not
  // on this one - this component's settings come from webform_components_supplier_card,
  // which has no generic_usps column. So it is looked up rather than read from
  // our own additionalSettings. Outside an S&C journey this is undefined and
  // behaviour is unchanged.
  const selectAndConnectComponent = getSelectAndConnectComponent({
    components,
  });
  const genericUsps =
    selectAndConnectComponent?.component?.additionalSettings?.generic_usps;

  const {
    badgeText,
    confirmationMessage,
    ctaButtonStyle,
    ctaLinkUrl,
    ctaText,
    logoUrl,
    selectedState,
    shouldForwardUrlParams,
    supplierName,
    usps,
    uspsSource,
  } = resolvePrimarySupplier({
    dynamicSelectAndConnect,
    dynamicMatches,
    additionalSettings: component?.component?.additionalSettings,
    componentText: component?.component?.text,
    genericUsps,
  });

  const hasReportedMissingUsps = useRef(false);
  useEffect(() => {
    // if there are no dynamicMatches then enableDynamicSelectAndConnect is off and / or there are no matches, thus no need to log
    if (hasReportedMissingUsps.current) return;
    if (!dynamicMatches?.length) return;

    hasReportedMissingUsps.current = true;
    reportWhereMissingUsps({
      dynamicSelectAndConnect,
      formId,
      subcategoryId,
      cards: [{ ...dynamicMatches[0], uspsSource }],
    });
  }, [
    dynamicMatches,
    dynamicSelectAndConnect,
    formId,
    subcategoryId,
    uspsSource,
  ]);

  const SupplierLogo = logoUrl ? (
    <img
      alt="supplier-logo"
      src={logoUrl}
      style={{ maxWidth: '100%', height: 'auto' }}
    />
  ) : undefined;

  const handleClick = () => {
    if (!ctaLinkUrl) return;

    const url = getLinkWithTrackingParameters({
      targetPageUrl: ctaLinkUrl,
      parentPageUrl: pageUrl,
      shouldForwardUrlParams,
    });

    // Attempt to open the link in a new tab
    window.open(url, '_blank');
  };

  return (
    <AnimationWrapper
      effect="fadeInUp"
      delay={400}
      animationTime={600}
      experimentName={animationExperimentName}
    >
      <Box marginTop={2}>
        <SupplierCardComponent
          badgeText={badgeText}
          SupplierLogo={SupplierLogo}
          supplierName={supplierName}
          ctaText={confirmationMessage || ctaText || 'Visit Site'}
          id={componentId}
          includeCta={!!ctaLinkUrl}
          invertCtaColors={ctaButtonStyle === 'outlined'}
          onClick={ctaLinkUrl ? handleClick : undefined}
          selected={selectedState || false}
          includeCheckbox={false}
          experimentName={supplierCardExperimentName}
        >
          <ul>
            {(usps || []).map((usp, index) => (
              <li key={`usp-${componentId}-${hashString(usp, index)}`}>
                {usp}
              </li>
            ))}
          </ul>
        </SupplierCardComponent>
      </Box>
    </AnimationWrapper>
  );
};

export default SupplierCard;
